<script lang="ts">
  import { onMount, tick } from 'svelte';
  import {
    LAYOUTS, WEEKDAYS_SHORT, isScheduledNow, scheduleLabel,
    type Angle, type AngleRole, type Channel, type LayoutId, type Match,
  } from '@multiview/shared';
  import { api, type ChannelGroup, type Fixture } from '../lib/api/client.ts';
  import LiveFixtures from './LiveFixtures.svelte';
  import StationSettings from './StationSettings.svelte';
  import { fixtureAngle } from '../lib/fixtureAngle.ts';
  import { isTv, maxTiles } from '../lib/platform.ts';
  import { remote } from '../lib/input/remote.ts';
  import { focusFirst } from '../lib/input/spatial.ts';

  interface Props {
    onWatch: (match: Match) => void;
    onLogout: () => void;
    onSettings: () => void;
  }
  const { onWatch, onLogout, onSettings }: Props = $props();

  let matches = $state<Match[]>([]);
  let channels = $state<Channel[]>([]);
  let groups = $state<ChannelGroup[]>([]);
  let total = $state(0);

  let query = $state('');
  let group = $state('');
  let favoritesOnly = $state(false);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let nowPlaying = $state<Record<number, { title: string; start: number; stop: number }>>({});
  let fixtures = $state<Fixture[]>([]);
  let fixturesLoading = $state(false);
  /** Channel suggestions per draft angle, keyed by angle id. */
  let suggestions = $state<Record<string, Array<{ channelId: number; name: string; reason: string; confidence: number }>>>({});
  let suggestFor = $state<string | null>(null);

  /** Angles being assembled. `id` is temporary until the match is saved. */
  let draft = $state<Array<Angle & { channelName: string }>>([]);
  let draftName = $state('');
  let draftLayout = $state<LayoutId>('1+3');
  let saving = $state(false);
  /**
   * Home shows the saved matches; everything about assembling one lives in the
   * editor. Three columns side by side were unusable from a sofa.
   */
  let mode = $state<'home' | 'editor' | 'stations'>('home');
  /** Match whose delete button is armed; a second press deletes. */
  let armedDelete = $state<number | null>(null);
  /** Set while a saved match is loaded into the draft; saving then updates it. */
  let editingId = $state<number | null>(null);
  /** Carried through an edit so saving does not drop a scheduled kickoff. */
  let draftKickoff = $state<number | null>(null);
  /** Recurring slot: "CL Dienstag" is highlighted on Tuesday evenings. */
  let draftWeekdays = $state<number[]>([]);
  let draftTime = $state<string | null>(null);

  /** Monday first, as a German week reads. */
  const WEEK = [1, 2, 3, 4, 5, 6, 0];
  /** Quarter hours from 10:00, which covers every kickoff slot in practice. */
  const TIMES = Array.from({ length: 56 }, (_, i) => {
    const m = 600 + i * 15;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  });

  function toggleWeekday(day: number) {
    draftWeekdays = draftWeekdays.includes(day)
      ? draftWeekdays.filter((d) => d !== day)
      : [...draftWeekdays, day].sort();
  }

  /** Re-evaluated every minute, so a match lights up when its evening starts. */
  let now = $state(new Date());
  onMount(() => {
    const timer = setInterval(() => { now = new Date(); }, 60_000);
    return () => clearInterval(timer);
  });

  /** Today's matches first; otherwise the order the proxy gives. */
  const sortedMatches = $derived.by(() => {
    const on = matches.filter((m) => isScheduledNow(m, now));
    return [...on, ...matches.filter((m) => !on.includes(m))];
  });

  const channelsById = $derived(new Map(channels.map((c) => [c.id, c])));

  async function loadMatches() {
    matches = await api.listMatches();
  }

  async function loadChannels() {
    loading = true;
    error = null;
    try {
      const page = await api.listChannels({
        q: query.trim() || undefined,
        group: group || undefined,
        favoritesOnly,
        limit: 120,
      });
      channels = page.channels;
      total = page.total;
      // What is on right now is the actual selection criterion before kickoff.
      // Missing EPG data is normal and must not break the list.
      void api.epgNow(page.channels.map((c) => c.id))
        .then((r) => { nowPlaying = r.now; })
        .catch(() => { nowPlaying = {}; });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  }

  /** Lands focus in the new screen, as App does for its own view changes. */
  async function show(next: 'home' | 'editor' | 'stations') {
    mode = next;
    await tick();
    const scope = document.querySelector<HTMLElement>('[data-focus-scope]') ?? document;
    focusFirst(scope);
  }

  function openEditor() {
    error = null;
    void show('editor');
  }

  /** An unsaved new match survives Back; edits to a saved one are dropped. */
  function closeEditor() {
    if (editingId !== null) resetDraft();
    void show('home');
  }

  onMount(() => {
    // Registered after App's handler, so it runs first and BACK leaves the
    // editor instead of falling through to the system.
    const unsubscribe = remote.subscribe((action) => {
      if (action.type !== 'back' || mode === 'home') return false;
      if (mode === 'editor') closeEditor();
      else void show('home');
      return true;
    });
    void loadMatches().then(() => {
      // Focus was placed before the list arrived, so it sits on "Neues Match".
      // Move it to the first saved match unless the user has already moved on.
      const a = document.activeElement;
      if (mode === 'home' && (a === document.body || a?.closest('.card.new'))) void show('home');
    });
    void loadChannels();
    void api.channelGroups().then((g) => { groups = g; }).catch(() => {});
    void api.fixtures().then((r) => { fixtures = r.fixtures; }).catch(() => {});
    return unsubscribe;
  });

  // Debounced so typing a channel name does not issue a query per keystroke.
  let searchTimer: number | null = null;
  $effect(() => {
    const _deps = [query, group, favoritesOnly];
    void _deps;
    if (searchTimer !== null) clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => void loadChannels(), 220);
    return () => { if (searchTimer !== null) clearTimeout(searchTimer); };
  });

  /**
   * One click on a running match should produce a ready angle: the right
   * channel, the fixture linked for goal switching, and a label that reads like
   * a match rather than like a channel name.
   */
  function addFixture(fixture: Fixture, channelId: number, channelName: string) {
    // On the TV a game on the start page plays straight away; more games are
    // added from the menu while watching. Setting up a match is the browser's job.
    if (isTv && mode === 'home') {
      onWatch({
        id: -1, name: `${fixture.homeTeam} – ${fixture.awayTeam}`, kickoff: fixture.kickoff, layout: '1',
        angles: [fixtureAngle(fixture, channelId)], weekdays: [], time: null, createdAt: Date.now(),
      });
      return;
    }
    if (mode === 'home') {
      // From the start page a game means "a new match for this", not an
      // addition to whatever saved match was last edited.
      if (editingId !== null) resetDraft();
      openEditor();
    }
    const existing = draft.find((a) => a.channelId === channelId);
    if (existing) {
      updateDraft(existing.id, {
        label: `${fixture.homeTeam} – ${fixture.awayTeam}`,
        fixtureProvider: fixture.provider,
        fixtureExternalId: fixture.externalId,
      });
      return;
    }

    draft = [...draft, {
      id: `draft-${channelId}-${Date.now()}`,
      label: `${fixture.homeTeam} – ${fixture.awayTeam}`,
      channelId,
      role: 'both',
      syncOffsetMs: 0,
      preferredQuality: 'auto',
      fixtureProvider: fixture.provider,
      fixtureExternalId: fixture.externalId,
      channelName,
    }];

    if (draftName.length === 0) draftName = `${fixture.homeTeam} – ${fixture.awayTeam}`;
  }

  function addToDraft(channel: Channel) {
    if (draft.some((a) => a.channelId === channel.id)) return;
    draft = [...draft, {
      id: `draft-${channel.id}-${Date.now()}`,
      label: channel.name,
      channelId: channel.id,
      role: 'both',
      syncOffsetMs: 0,
      preferredQuality: 'auto',
      fixtureProvider: null,
      fixtureExternalId: null,
      channelName: channel.name,
    }];
    if (draftName.length === 0) draftName = channel.name;
  }

  function removeFromDraft(id: string) {
    draft = draft.filter((a) => a.id !== id);
  }

  function updateDraft(id: string, patch: Partial<Angle>) {
    draft = draft.map((a) => (a.id === id ? { ...a, ...patch } : a));
  }

  const draftVisible = $derived(draft.filter((a) => a.role !== 'audio').length);
  const overTileBudget = $derived(draftVisible > maxTiles);

  function resetDraft() {
    draft = [];
    draftName = '';
    draftLayout = '1+3';
    draftKickoff = null;
    draftWeekdays = [];
    draftTime = null;
    editingId = null;
    suggestions = {};
  }

  /**
   * Loads a saved match into the draft panel. Channel names come from a lookup
   * by id, because the angles' channels are rarely in the current search result.
   */
  async function editMatch(match: Match) {
    if (editingId !== match.id) {
      if (editingId === null && draft.length > 0 && !confirm('Der angefangene neue Match wird verworfen. Fortfahren?')) return;
      await loadIntoDraft(match);
    }
    openEditor();
  }

  async function loadIntoDraft(match: Match) {
    draft = match.angles.map((a) => ({ ...a, channelName: '' }));
    draftName = match.name;
    draftLayout = match.layout;
    draftKickoff = match.kickoff;
    draftWeekdays = [...match.weekdays];
    draftTime = match.time;
    editingId = match.id;
    suggestions = {};
    error = null;

    if (match.angles.length === 0) return;
    try {
      const page = await api.listChannels({ ids: [...new Set(match.angles.map((a) => a.channelId))].join(',') });
      const names = new Map(page.channels.map((c) => [c.id, c.name]));
      // A source refresh can drop a channel; say so instead of showing nothing.
      draft = draft.map((a) => ({ ...a, channelName: names.get(a.channelId) ?? 'Kanal nicht mehr vorhanden' }));
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
  }

  function toMatch(id: number): Match {
    return {
      id,
      name: draftName.trim() || 'Multiview',
      kickoff: draftKickoff,
      layout: draftLayout,
      angles: draft.map(({ channelName: _n, ...angle }) => angle),
      weekdays: draftWeekdays,
      time: draftWeekdays.length > 0 ? draftTime : null,
      createdAt: Date.now(),
    };
  }

  /** Watch without persisting -- the common case ten minutes before kickoff. */
  function startAdHoc() {
    if (draft.length === 0) return;
    onWatch(toMatch(-1));
  }

  async function save(watch: boolean) {
    if (draft.length === 0) return;
    saving = true;
    error = null;
    try {
      const input = {
        name: draftName.trim() || 'Multiview',
        kickoff: draftKickoff,
        layout: draftLayout,
        weekdays: draftWeekdays,
        time: draftWeekdays.length > 0 ? draftTime : null,
        // Saved angles keep their id; temporary draft ids are left to the proxy.
        angles: draft.map(({ channelName: _n, id, ...angle }) => (
          id.startsWith('draft-') ? angle : { ...angle, id }
        )),
      };
      const saved = editingId === null
        ? await api.createMatch(input)
        : await api.updateMatch(editingId, input);
      await loadMatches();
      resetDraft();
      if (watch) onWatch(saved);
      else void show('home');
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  async function deleteMatch(match: Match) {
    // Two presses instead of a native dialog, which a TV remote handles badly.
    if (armedDelete !== match.id) {
      armedDelete = match.id;
      return;
    }
    armedDelete = null;
    await api.deleteMatch(match.id);
    if (editingId === match.id) resetDraft();
    await loadMatches();
  }

  async function toggleFavorite(channel: Channel) {
    await api.setFavorite(channel.id, !channel.favorite);
    channels = channels.map((c) => (c.id === channel.id ? { ...c, favorite: !c.favorite } : c));
  }

  const fixtureLabel = (f: Fixture) => {
    const t = new Date(f.kickoff).toLocaleString('de-DE', {
      weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
    const tv = f.channel ? ` · ▶ ${f.channel.name}` : f.broadcasters?.length ? ` · ${f.broadcasters.slice(0, 3).join(', ')}` : '';
    return `${t}  ${f.homeTeam} – ${f.awayTeam}${tv}`;
  };

  async function reloadFixtures() {
    fixturesLoading = true;
    try {
      await api.refreshFixtures();
      fixtures = (await api.fixtures()).fixtures;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      fixturesLoading = false;
    }
  }

  /**
   * Which of the user's own channels is showing this fixture. There is no free
   * API for that, so the answer comes from their own EPG, searched by team name.
   */
  async function findChannels(angleId: string, provider: string, externalId: string) {
    suggestFor = angleId;
    try {
      const r = await api.fixtureChannels(provider, externalId);
      suggestions = { ...suggestions, [angleId]: r.suggestions };
      if (r.suggestions.length === 0) {
        error = r.epgAvailable
          ? 'Im EPG rund um den Anstoß steht keine Sendung, die beide Mannschaften nennt.'
          : 'Für diesen Zeitraum liegen keine EPG-Daten vor.';
      } else {
        error = null;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      suggestFor = null;
    }
  }

  const ROLES: Array<{ value: AngleRole; label: string }> = [
    { value: 'both', label: 'Bild + Ton' },
    { value: 'video', label: 'nur Bild' },
    { value: 'audio', label: 'nur Ton' },
  ];
</script>

<div class="browse" data-mode={mode}>
  {#if mode === 'home'}
    <header class="top">
      <h1>Multiview</h1>
      <button data-focusable type="button" class="plain" onclick={() => void show('stations')}>Sender</button>
      <button data-focusable type="button" class="plain" onclick={onSettings}>Einstellungen</button>
      <button data-focusable type="button" class="plain" onclick={onLogout}>Abmelden</button>
    </header>

    <section class="matches scroll">
      <h2>Deine Matches</h2>
      <ul class="cards">
        <li class="card new">
          <button
            class="open"
            data-focusable
            data-focus-default={matches.length === 0 ? true : undefined}
            onclick={() => { if (editingId !== null) resetDraft(); openEditor(); }}
          >
            <span class="plus">+</span>
            <span class="name">{editingId === null && draft.length > 0 ? 'Entwurf fortsetzen' : 'Neues Match'}</span>
            {#if editingId === null && draft.length > 0}
              <span class="faint">{draft.length} {draft.length === 1 ? 'Perspektive' : 'Perspektiven'} ausgewählt</span>
            {/if}
          </button>
        </li>
        {#each sortedMatches as match, i (match.id)}
          {@const today = isScheduledNow(match, now)}
          <li class="card" class:today>
            <button
              class="open"
              data-focusable
              data-focus-default={i === 0 ? true : undefined}
              onclick={() => onWatch(match)}
            >
              {#if today}
                <span class="badge">Heute{match.time ? ` ${match.time}` : ''}</span>
              {/if}
              <span class="name">{match.name}</span>
              <span class="faint">{match.angles.map((a) => a.label).join(' · ') || 'keine Perspektiven'}</span>
              <span class="faint small">
                {LAYOUTS[match.layout].label}{match.weekdays.length > 0 ? ` · ${scheduleLabel(match)}` : ''}
              </span>
            </button>
            <div class="card-actions">
              <button data-focusable aria-label={`${match.name} bearbeiten`} onclick={() => editMatch(match)}>Bearbeiten</button>
              <button
                data-focusable
                class:armed={armedDelete === match.id}
                aria-label={`${match.name} löschen`}
                onclick={() => deleteMatch(match)}
                onblur={() => { if (armedDelete === match.id) armedDelete = null; }}
              >{armedDelete === match.id ? 'Wirklich löschen?' : 'Löschen'}</button>
            </div>
          </li>
        {/each}
      </ul>
      {#if matches.length === 0}
        <p class="faint">Noch keine gespeicherten Matches. Lege eins an oder wähle unten ein laufendes Spiel.</p>
      {/if}
    </section>

    <LiveFixtures onAdd={addFixture} usedChannelIds={draft.map((a) => a.channelId)} />

  {:else if mode === 'stations'}
    <header class="top">
      <button data-focusable type="button" class="plain" onclick={() => void show('home')}>← Zurück</button>
      <h1>Sender zuordnen</h1>
    </header>
    <StationSettings />

  {:else}
    <header class="top">
      <button data-focusable type="button" class="plain" onclick={closeEditor}>← Zurück</button>
      <h1>{editingId === null ? 'Neues Match' : 'Match bearbeiten'}</h1>
      <div class="draft-actions">
        {#if editingId === null}
          <button
            class="primary"
            data-focusable
            data-focus-default={draft.length > 0 ? true : undefined}
            disabled={draft.length === 0}
            onclick={startAdHoc}
          >Jetzt ansehen</button>
          <button data-focusable disabled={saving || draft.length === 0} onclick={() => save(true)}>
            {saving ? 'Speichere …' : 'Speichern + ansehen'}
          </button>
          <button data-focusable disabled={draft.length === 0} onclick={resetDraft}>Leeren</button>
        {:else}
          <button
            class="primary"
            data-focusable
            data-focus-default={draft.length > 0 ? true : undefined}
            disabled={saving || draft.length === 0}
            onclick={() => save(false)}
          >{saving ? 'Speichere …' : 'Speichern'}</button>
          <button data-focusable disabled={saving || draft.length === 0} onclick={() => save(true)}>Speichern + ansehen</button>
        {/if}
      </div>
    </header>

    <LiveFixtures onAdd={addFixture} usedChannelIds={draft.map((a) => a.channelId)} />

    <div class="editor">
      <section class="panel channels">
        <h2>Kanäle <span class="faint">{total}</span></h2>

      <div class="filters">
        <input bind:value={query} data-focusable data-focus-default={!isTv && draft.length === 0 ? true : undefined} placeholder="Suchen …" aria-label="Kanäle suchen" />
        <select bind:value={group} data-focusable aria-label="Gruppe">
          <option value="">Alle Gruppen</option>
          {#each groups as g (g.name)}
            <option value={g.name}>{g.name} ({g.count})</option>
          {/each}
        </select>
        <label class="check">
          <input type="checkbox" bind:checked={favoritesOnly} data-focusable />
          <span>Nur Favoriten</span>
        </label>
      </div>

      {#if error}<p class="error">{error}</p>{/if}

      <ul class="list scroll" class:dim={loading}>
        {#each channels as channel, i (channel.id)}
          <li>
            <button
              class="row"
              data-focusable
              data-focus-default={isTv && draft.length === 0 && i === 0 ? true : undefined}
              onclick={() => addToDraft(channel)}
            >
              <span class="name">{channel.name}</span>
              {#if nowPlaying[channel.id]}
                <span class="now">{nowPlaying[channel.id]!.title}</span>
              {/if}
              <span class="faint">{channel.group ?? '—'} · {channel.streamKind}</span>
            </button>
            <button
              class="icon"
              data-focusable
              class:on={channel.favorite}
              aria-label={`${channel.name} als Favorit`}
              onclick={() => toggleFavorite(channel)}
            >★</button>
          </li>
        {/each}
        {#if channels.length === 0 && !loading}
          <li class="faint pad">Keine Treffer.</li>
        {/if}
      </ul>
      </section>

      <section class="panel draft scroll">
        <h2>Perspektiven <span class="faint">{draft.length}</span></h2>

        {#if draft.length === 0 && editingId === null}
          <p class="faint">Links Kanäle anklicken oder oben ein laufendes Spiel wählen.</p>
        {:else}
          <label class="field">
            <span>Name</span>
            <input bind:value={draftName} data-focusable placeholder="Bayern – Dortmund" />
          </label>

          <div class="field">
            <span>Wochentage <span class="faint">– an diesen Tagen steht das Match vorne</span></span>
            <div class="weekdays">
              {#each WEEK as day (day)}
                <button
                  type="button"
                  data-focusable
                  aria-pressed={draftWeekdays.includes(day)}
                  class:on={draftWeekdays.includes(day)}
                  onclick={() => toggleWeekday(day)}
                >{WEEKDAYS_SHORT[day]}</button>
              {/each}
              <select
                data-focusable
                aria-label="Uhrzeit"
                disabled={draftWeekdays.length === 0}
                value={draftTime ?? ''}
                onchange={(e) => { draftTime = e.currentTarget.value || null; }}
              >
                <option value="">ganzer Tag</option>
                {#if draftTime && !TIMES.includes(draftTime)}<option value={draftTime}>{draftTime}</option>{/if}
                {#each TIMES as t (t)}<option value={t}>ab {t}</option>{/each}
              </select>
            </div>
          </div>

          <label class="field">
            <span>Layout</span>
            <select bind:value={draftLayout} data-focusable>
              {#each Object.values(LAYOUTS) as def (def.id)}
                <option value={def.id} disabled={def.slots > maxTiles}>
                  {def.label} ({def.slots}){def.slots > maxTiles ? ' – zu viele Kacheln' : ''}
                </option>
              {/each}
            </select>
          </label>

          <ul class="list angles">
            {#each draft as angle (angle.id)}
              <li class="angle">
                <input
                  value={angle.label}
                  data-focusable
                  aria-label="Bezeichnung"
                  oninput={(e) => updateDraft(angle.id, { label: e.currentTarget.value })}
                />
                <div class="angle-row">
                  <select
                    value={angle.role}
                    data-focusable
                    aria-label="Rolle"
                    onchange={(e) => updateDraft(angle.id, { role: e.currentTarget.value as AngleRole })}
                  >
                    {#each ROLES as role (role.value)}
                      <option value={role.value}>{role.label}</option>
                    {/each}
                  </select>
                  <span class="offset-field">
                    <input
                      type="number" min="0" max="60" step="0.5"
                      value={angle.syncOffsetMs / 1000}
                      data-focusable
                      aria-label="Verzögerung in Sekunden"
                      oninput={(e) => updateDraft(angle.id, { syncOffsetMs: Math.round(Number(e.currentTarget.value) * 1000) })}
                    />
                    <span class="faint">s</span>
                  </span>
                  <button class="icon" data-focusable aria-label="Entfernen" onclick={() => removeFromDraft(angle.id)}>✕</button>
                </div>
                <span class="faint small">{channelsById.get(angle.channelId)?.name ?? angle.channelName}</span>

                <select
                  value={angle.fixtureExternalId ?? ''}
                  data-focusable
                  aria-label="Welches Spiel zeigt diese Perspektive?"
                  onchange={(e) => {
                    const id = e.currentTarget.value;
                    const f = fixtures.find((x) => x.externalId === id);
                    updateDraft(angle.id, {
                      fixtureExternalId: id || null,
                      fixtureProvider: f?.provider ?? null,
                    });
                  }}
                >
                  <option value="">kein Spiel zugeordnet</option>
                  {#each fixtures as f (f.provider + f.externalId)}
                    <option value={f.externalId}>{fixtureLabel(f)}</option>
                  {/each}
                </select>

                {#if angle.fixtureExternalId && angle.fixtureProvider}
                  <button
                    class="link"
                    data-focusable
                    type="button"
                    disabled={suggestFor === angle.id}
                    onclick={() => findChannels(angle.id, angle.fixtureProvider!, angle.fixtureExternalId!)}
                  >
                    {suggestFor === angle.id ? 'suche …' : 'Wer zeigt das Spiel?'}
                  </button>
                {/if}

                {#if suggestions[angle.id]?.length}
                  <ul class="suggestions">
                    {#each suggestions[angle.id]! as s (s.channelId)}
                      <li>
                        <button
                          class="row"
                          data-focusable
                          onclick={() => updateDraft(angle.id, { channelId: s.channelId })}
                          class:chosen={angle.channelId === s.channelId}
                        >
                          <span class="name">{s.name}</span>
                          <span class="faint">{Math.round(s.confidence * 100)}% · {s.reason}</span>
                        </button>
                      </li>
                    {/each}
                  </ul>
                {/if}
              </li>
            {/each}
          </ul>

          {#if overTileBudget}
            <p class="warn">
              {draftVisible} Bildkacheln, aber diese Plattform trägt {maxTiles}.
              Das Layout wird beim Ansehen begrenzt; überzählige Perspektiven bleiben per ◀▶ erreichbar.
            </p>
          {/if}

          {#if draft.length === 0}
            <p class="faint">Keine Perspektiven mehr. Links Kanäle anklicken, um welche hinzuzufügen.</p>
          {/if}

          <p class="faint small hinweis">
            Ein zugeordnetes Spiel erlaubt das Umschalten bei Toren — zurückgehalten, bis dein
            Bild den Moment erreicht hat.
            <button class="link" data-focusable type="button" disabled={fixturesLoading} onclick={reloadFixtures}>
              {fixturesLoading ? 'lädt …' : `Spiele neu laden (${fixtures.length})`}
            </button>
          </p>

        {/if}
      </section>
    </div>
  {/if}
</div>

<style>
  .browse {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    padding: var(--safe-inset);
    overflow: hidden;
  }

  .top { display: flex; align-items: center; gap: var(--gap); flex: 0 0 auto; }
  .top h1 { margin: 0; font-size: var(--font-xl); flex: 1 1 auto; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  [data-mode='editor'] .top h1 { font-size: var(--font-lg); }

  /* ------------------------------------------------------------- home */

  /* Matches are why the page is opened; they get the space and the focus. */
  .matches {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    /* Room for the focus ring of the outer cards. */
    padding: 6px;
    margin: -6px;
  }

  .cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(18em, 1fr));
    gap: calc(var(--gap) * 1.5);
  }

  .card {
    display: flex;
    flex-direction: column;
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    min-width: 0;
  }

  .card .open {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
    background: transparent;
    border: none;
    padding: var(--pad);
    min-height: 6em;
    min-width: 0;
  }

  .card .open:hover { background: rgba(255, 255, 255, 0.04); }
  .card .name { font-size: var(--font-lg); font-weight: 600; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .card .faint { font-size: var(--font-sm); max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .card-actions { display: flex; gap: 4px; padding: 0 calc(var(--pad) * 0.5) calc(var(--pad) * 0.5); }
  .card-actions button { font-size: var(--font-sm); background: transparent; padding: 0.3em 0.7em; }
  /* Wider than its neighbour so D-pad down from the card lands on it, not on Löschen. */
  .card-actions button:first-child { flex: 2 1 0; }
  .card-actions button:last-child { flex: 1 1 0; }
  .card-actions button.armed { color: var(--danger); border-color: var(--danger); }

  /* Today's match: what you came to watch tonight. */
  .card.today { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent), 0 0 24px rgba(55, 214, 122, 0.18); }
  .badge {
    align-self: flex-start;
    font-size: var(--font-sm);
    font-weight: 600;
    color: #04130a;
    background: var(--accent);
    border-radius: 4px;
    padding: 0.05em 0.5em;
  }

  .weekdays { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
  .weekdays button { min-width: 2.8em; padding: 0.35em 0.5em; background: transparent; }
  .weekdays button.on { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }
  .weekdays select { width: auto; flex: 1 1 8em; }

  .card.new { border-style: dashed; border-color: var(--border-strong); }
  .card.new .open { justify-content: center; }
  .card.new .plus { font-size: var(--font-xl); line-height: 1; color: var(--accent); }

  /* ----------------------------------------------------------- editor */

  .editor {
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--gap);
  }

  @media (max-width: 900px) {
    .browse { overflow-y: auto; }
    .top { flex-wrap: wrap; }
    .editor { grid-template-columns: 1fr; flex: 0 0 auto; }
    .matches { flex: 0 0 auto; }
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    min-height: 0;
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: var(--pad);
  }

  .channels { min-height: 0; }

  .plain { background: transparent; border-color: var(--border); font-size: var(--font-sm); }
  h2 { margin: 0; font-size: var(--font-lg); }
  .matches h2 { font-size: var(--font-base); color: var(--text-dim); font-weight: 600; }

  .filters { display: flex; flex-direction: column; gap: var(--gap); }
  .check { display: flex; align-items: center; gap: 8px; font-size: var(--font-sm); }
  .check input { width: auto; }

  .list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; min-height: 0; }
  .list li { display: flex; gap: 4px; align-items: stretch; }

  .row {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    text-align: left;
    background: transparent;
    border-color: transparent;
    min-width: 0;
  }

  .row:hover { background: rgba(255, 255, 255, 0.05); }
  .row .name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
  .row .faint { font-size: var(--font-sm); }
  .row .now {
    font-size: var(--font-sm);
    color: var(--accent);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .icon { background: transparent; border-color: transparent; padding: 0.4em 0.6em; }
  .icon.on { color: var(--warn); }

  .dim { opacity: 0.55; }
  .pad { padding: var(--pad) 0; }

  .field { display: flex; flex-direction: column; gap: 4px; }
  .field > span { font-size: var(--font-sm); color: var(--text-dim); }

  /* The generic list may shrink so the channel column can scroll; the angle
     editor must not -- shrinking it made its rows overflow and overlap the text
     below. */
  .angles { min-height: auto; flex: 0 0 auto; }

  .angles .angle {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    padding: var(--gap);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  .angle-row { display: flex; gap: 4px; align-items: center; }
  .angle-row select { flex: 1 1 auto; min-width: 0; }
  .offset-field { display: inline-flex; align-items: center; gap: 4px; }
  .offset-field input { width: 5em; }
  .small { font-size: var(--font-sm); }
  .hinweis { margin: var(--gap) 0 0; line-height: 1.45; }

  .suggestions { list-style: none; margin: 4px 0 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .suggestions .row { border: 1px solid var(--border); }
  .suggestions .row.chosen { border-color: var(--accent); }

  .link {
    background: none;
    border: none;
    /* Needs its own line box: inline with zero padding it rode up into the
       paragraph below. */
    display: inline-block;
    padding: 2px 0;
    margin: 2px 0;
    color: var(--accent);
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
    text-align: left;
  }

  .draft-actions { display: flex; gap: var(--gap); flex-wrap: wrap; justify-content: flex-end; }
  .primary { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }

  .error { color: var(--danger); margin: 0; }
  .warn { color: var(--warn); margin: 0; font-size: var(--font-sm); line-height: 1.4; }
</style>
